import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { staffAPI, designationAPI, visaProgramAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  UserPlus, Users, Search, Shield, Trash2, Edit,
  CheckCircle, XCircle, Eye, EyeOff, Loader2, Plus,
  ChevronDown, ChevronUp, Briefcase, Tag, X, Save,
  DollarSign, Settings, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Constants ────────────────────────────────────────────────────────────────

const STAFF_ROLES = [
  { value: 'super_admin', label: 'Super Admin', color: 'bg-purple-100 text-purple-700' },
  { value: 'admin', label: 'Admin', color: 'bg-blue-100 text-blue-700' },
  { value: 'process_manager', label: 'Process Manager', color: 'bg-sky-100 text-sky-700' },
  { value: 'case_officer', label: 'Case Officer', color: 'bg-slate-100 text-slate-700' },
  { value: 'accountant', label: 'Accountant', color: 'bg-green-100 text-green-700' },
  { value: 'sales_consultant', label: 'Sales Consultant', color: 'bg-violet-100 text-violet-700' },
  { value: 'sales_manager', label: 'Sales Manager', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'marketing_manager', label: 'Marketing Manager', color: 'bg-pink-100 text-pink-700' },
  { value: 'legal_counsel',    label: 'Legal Counsel',     color: 'bg-red-100 text-red-700' },
];

const ROLE_COLORS = {
  super_admin: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  process_manager: 'bg-sky-100 text-sky-700',
  case_officer: 'bg-slate-100 text-slate-700',
  accountant: 'bg-green-100 text-green-700',
  sales_consultant: 'bg-violet-100 text-violet-700',
  sales_manager: 'bg-indigo-100 text-indigo-700',
  marketing_manager: 'bg-pink-100 text-pink-700',
  legal_counsel: 'bg-red-100 text-red-700',
};

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  process_manager: 'Process Manager',
  case_officer: 'Case Officer',
  accountant: 'Accountant',
  sales_consultant: 'Sales Consultant',
  sales_manager: 'Sales Manager',
  marketing_manager: 'Marketing Manager',
  legal_counsel: 'Legal Counsel',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const emptyCompensation = (empType = 'full_time', role = 'case_officer') => ({
  employment_type: empType,
  currency: 'INR',
  monthly_salary: '',
  salary_payment_day: 1,
  contractor_payment_model: role === 'case_officer' ? 'per_case' : 'per_close',
  per_case_rates: [],
  payment_terms: null,
  postpayment_trigger: 'case_closure',
  trigger_stage: null,
  per_close_amount: '',
  commission_tiers: [{ min_volume: 0, max_volume: '', commission_amount: '' }],
});

const emptyForm = {
  first_name: '',
  last_name: '',
  date_of_birth: '',
  official_email: '',
  personal_email: '',
  official_phone: '',
  personal_phone: '',
  password: '',
  role: 'case_officer',
  designation_id: '',
  compensation: emptyCompensation('full_time', 'case_officer'),
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
        <Icon size={16} className="text-slate-600" />
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-800">{title}</p>
        {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      </div>
    </div>
  );
}

function FieldGroup({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none bg-white";
const disabledInputCls = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-500 cursor-not-allowed";

// ─── Compensation Section ─────────────────────────────────────────────────────

function CompensationSection({ comp, onChange, role, visaPrograms }) {
  const isFT = comp.employment_type === 'full_time';
  const isContractorCO = !isFT && role === 'case_officer';
  const isContractorSales = !isFT && role !== 'case_officer';

  const updateField = (field, val) => onChange({ ...comp, [field]: val });

  const addProgramRate = (prog) => {
    const exists = (comp.per_case_rates || []).find(r => r.visa_program_id === prog.id);
    if (exists) return;
    onChange({
      ...comp,
      per_case_rates: [
        ...(comp.per_case_rates || []),
        { visa_program_id: prog.id, visa_type: prog.visa_type, country: prog.country, amount: '', currency: 'INR' },
      ],
    });
  };

  const updateRate = (idx, field, val) => {
    const rates = [...(comp.per_case_rates || [])];
    rates[idx] = { ...rates[idx], [field]: val };
    onChange({ ...comp, per_case_rates: rates });
  };

  const removeRate = (idx) => {
    const rates = [...(comp.per_case_rates || [])];
    rates.splice(idx, 1);
    onChange({ ...comp, per_case_rates: rates });
  };

  const addTier = () => {
    onChange({ ...comp, commission_tiers: [...(comp.commission_tiers || []), { min_volume: '', max_volume: '', commission_amount: '' }] });
  };

  const updateTier = (idx, field, val) => {
    const tiers = [...(comp.commission_tiers || [])];
    tiers[idx] = { ...tiers[idx], [field]: val };
    onChange({ ...comp, commission_tiers: tiers });
  };

  const removeTier = (idx) => {
    const tiers = [...(comp.commission_tiers || [])];
    tiers.splice(idx, 1);
    onChange({ ...comp, commission_tiers: tiers });
  };

  const availablePrograms = visaPrograms.filter(
    p => !(comp.per_case_rates || []).find(r => r.visa_program_id === p.id)
  );

  return (
    <div className="space-y-4">
      <SectionHeader icon={DollarSign} title="Compensation" subtitle="Salary, contract rates, or commissions" />

      {/* Employment Type Toggle */}
      <div className="flex gap-3">
        {['full_time', 'independent_contractor'].map(et => (
          <button
            key={et}
            type="button"
            onClick={() => onChange({ ...emptyCompensation(et, role), employment_type: et })}
            className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-all ${
              comp.employment_type === et
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
            }`}
          >
            {et === 'full_time' ? 'Full-Time Employee' : 'Independent Contractor'}
          </button>
        ))}
      </div>

      {/* Full-Time: Salary */}
      {isFT && (
        <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <FieldGroup label="Monthly Salary (₹)" required>
            <input type="number" min="0" placeholder="e.g. 45000" value={comp.monthly_salary || ''} onChange={e => updateField('monthly_salary', e.target.value)} className={inputCls} />
          </FieldGroup>
          <FieldGroup label="Salary Payment Day" required>
            <select value={comp.salary_payment_day || 1} onChange={e => updateField('salary_payment_day', parseInt(e.target.value))} className={inputCls}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                <option key={d} value={d}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'} of every month</option>
              ))}
            </select>
          </FieldGroup>
          <div className="col-span-2 text-xs text-slate-500 flex items-center gap-1">
            <CheckCircle size={12} className="text-green-500" />
            A recurring expense entry will be auto-created in Accounting → Expenses
          </div>
        </div>
      )}

      {/* Contractor — Case Officer: Per-Case Rates + Payment Terms */}
      {isContractorCO && (
        <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Per-Case Rate by Visa Program</p>
          {availablePrograms.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {availablePrograms.map(p => (
                <button key={p.id} type="button" onClick={() => addProgramRate(p)} className="px-2 py-1 bg-white border border-slate-200 text-xs rounded-lg hover:border-slate-400 transition-all">
                  + {p.visa_type} — {p.country}
                </button>
              ))}
            </div>
          )}
          {(comp.per_case_rates || []).length === 0 && (
            <p className="text-xs text-slate-400 italic">Add rates for each visa program above.</p>
          )}
          {(comp.per_case_rates || []).map((rate, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
              <div className="flex-1 text-sm font-medium text-slate-700">{rate.visa_type} — {rate.country}</div>
              <span className="text-sm text-slate-500">₹</span>
              <input type="number" min="0" placeholder="Amount" value={rate.amount || ''} onChange={e => updateRate(idx, 'amount', e.target.value)} className="w-28 px-2 py-1 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-slate-900/20 outline-none" />
              <span className="text-xs text-slate-400">per file</span>
              <button type="button" onClick={() => removeRate(idx)} className="p-1 text-red-400 hover:bg-red-50 rounded">
                <X size={14} />
              </button>
            </div>
          ))}

          {/* Payment Terms */}
          <div className="pt-2 border-t border-slate-200">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Payment Terms</p>
            <div className="flex gap-3 mb-3">
              {[
                { value: 'prepayment', label: 'Prepayment', desc: 'Pay before work begins' },
                { value: 'postpayment', label: 'Postpayment', desc: 'Pay after work is done' },
              ].map(pt => (
                <button key={pt.value} type="button"
                  onClick={() => onChange({ ...comp, payment_terms: pt.value })}
                  className={`flex-1 py-2.5 px-3 rounded-lg border text-left transition-all ${
                    comp.payment_terms === pt.value
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                  }`}
                >
                  <p className="text-sm font-medium">{pt.label}</p>
                  <p className={`text-xs mt-0.5 ${comp.payment_terms === pt.value ? 'text-slate-300' : 'text-slate-400'}`}>{pt.desc}</p>
                </button>
              ))}
            </div>

            {comp.payment_terms === 'postpayment' && (
              <div className="space-y-2">
                <p className="text-xs text-slate-600 font-medium">Payment trigger:</p>
                <div className="flex gap-2">
                  {[
                    { value: 'case_closure', label: 'On Case Closure' },
                    { value: 'stage_reached', label: 'On Stage Reached' },
                  ].map(t => (
                    <button key={t.value} type="button"
                      onClick={() => onChange({ ...comp, postpayment_trigger: t.value })}
                      className={`flex-1 py-1.5 px-3 rounded-lg border text-xs font-medium transition-all ${
                        comp.postpayment_trigger === t.value
                          ? 'bg-amber-600 text-white border-amber-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                {comp.postpayment_trigger === 'stage_reached' && (
                  <select value={comp.trigger_stage || ''} onChange={e => onChange({ ...comp, trigger_stage: e.target.value })} className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-slate-900/20 outline-none mt-1">
                    <option value="">Select trigger stage…</option>
                    {['Agreement Signed','Documents Submitted','Application Filed','Decision Pending','Approved','Closed'].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {!comp.payment_terms && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <span>⚠</span> Select payment terms so the system knows when to log contractor expenses.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Contractor — Sales: Commission Model */}
      {isContractorSales && (
        <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Commission Model</p>
          <div className="flex gap-3">
            {[
              { value: 'per_close', label: 'Fixed per Close' },
              { value: 'tiered_volume', label: 'Tiered by Volume' },
            ].map(m => (
              <button key={m.value} type="button"
                onClick={() => updateField('contractor_payment_model', m.value)}
                className={`flex-1 py-2 px-3 rounded-lg border text-sm transition-all ${comp.contractor_payment_model === m.value ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {comp.contractor_payment_model === 'per_close' && (
            <FieldGroup label="Amount per Closed Sale (₹)" required>
              <input type="number" min="0" placeholder="e.g. 2000" value={comp.per_close_amount || ''} onChange={e => updateField('per_close_amount', e.target.value)} className={inputCls} />
            </FieldGroup>
          )}

          {comp.contractor_payment_model === 'tiered_volume' && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">Define commission brackets by total sales volume achieved in a month.</p>
              {(comp.commission_tiers || []).map((tier, idx) => (
                <div key={idx} className="grid grid-cols-3 gap-2 items-center bg-white border border-slate-200 rounded-lg p-2">
                  <div>
                    <label className="text-xs text-slate-500">From (₹)</label>
                    <input type="number" min="0" value={tier.min_volume || ''} onChange={e => updateTier(idx, 'min_volume', e.target.value)} placeholder="0" className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-slate-900/20 outline-none mt-0.5" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">To (₹, blank = ∞)</label>
                    <input type="number" min="0" value={tier.max_volume || ''} onChange={e => updateTier(idx, 'max_volume', e.target.value)} placeholder="No limit" className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-slate-900/20 outline-none mt-0.5" />
                  </div>
                  <div className="flex items-end gap-1">
                    <div className="flex-1">
                      <label className="text-xs text-slate-500">Commission (₹)</label>
                      <input type="number" min="0" value={tier.commission_amount || ''} onChange={e => updateTier(idx, 'commission_amount', e.target.value)} placeholder="0" className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-slate-900/20 outline-none mt-0.5" />
                    </div>
                    {(comp.commission_tiers || []).length > 1 && (
                      <button type="button" onClick={() => removeTier(idx)} className="p-1.5 text-red-400 hover:bg-red-50 rounded mb-0.5">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <button type="button" onClick={addTier} className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 transition-all">
                <Plus size={12} /> Add Tier
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Designation Panel ────────────────────────────────────────────────────────

function DesignationPanel({ onClose }) {
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => { fetchDesignations(); }, []);

  const fetchDesignations = async () => {
    setLoading(true);
    try {
      const res = await designationAPI.getDesignations();
      setDesignations(res.data || []);
    } catch (e) {
      setError('Failed to load designations: ' + (e.response?.data?.detail || e.message || 'Unknown error'));
    } finally { setLoading(false); }
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setError('');
    setSuccessMsg('');
    setSaving(true);
    try {
      await designationAPI.createDesignation({ name });
      setSuccessMsg(`"${name}" added successfully`);
      setNewName('');
      await fetchDesignations();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to create designation';
      setError(msg);
      toast.error(msg);
      console.error('Designation create error:', e.response?.data || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}"?`)) return;
    setError('');
    try {
      await designationAPI.deleteDesignation(id);
      setDesignations(prev => prev.filter(d => d.id !== id));
    } catch (e) {
      setError('Failed to remove designation');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag size={20} className="text-slate-700" />
            <h2 className="text-lg font-bold text-slate-900">Manage Designations</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-2">
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="e.g. Senior Consultant"
              className={inputCls + " flex-1"}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={saving || !newName.trim()}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5 text-sm font-medium whitespace-nowrap"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {saving ? 'Adding...' : 'Add'}
            </button>
          </div>

          {error && (
            <div className="px-4 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
              <AlertTriangle size={14} className="flex-shrink-0" />
              {error}
            </div>
          )}
          {successMsg && (
            <div className="px-4 py-2.5 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              {successMsg}
            </div>
          )}

          {loading ? (
            <div className="text-center py-6"><Loader2 size={24} className="animate-spin text-slate-400 mx-auto" /></div>
          ) : designations.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">No designations yet. Add your first one above.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {designations.map(d => (
                <div key={d.id} className="flex items-center justify-between px-4 py-2.5 bg-slate-50 rounded-lg border border-slate-200">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{d.name}</p>
                    {d.department && <p className="text-xs text-slate-500">{d.department}</p>}
                  </div>
                  <button type="button" onClick={() => handleDelete(d.id, d.name)} className="p-1.5 text-red-400 hover:bg-red-50 rounded transition-all">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function UsersManagement() {
  const { user: currentUser } = useAuth();
  const [staff, setStaff] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [visaPrograms, setVisaPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDesignationPanel, setShowDesignationPanel] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const isSuperAdmin = currentUser?.role === 'super_admin';
  const canManageStaff = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'process_manager';

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [staffRes, desigRes, vpRes] = await Promise.all([
        staffAPI.getStaff(),
        designationAPI.getDesignations(),
        visaProgramAPI.getPrograms(),
      ]);
      setStaff(staffRes.data || []);
      setDesignations(desigRes.data || []);
      setVisaPrograms(vpRes.data || []);
    } catch { toast.error('Failed to load staff data'); }
    finally { setLoading(false); }
  };

  const openAdd = () => {
    setForm({ ...emptyForm });
    setShowPassword(false);
    setShowAddModal(true);
  };

  const openEdit = (member) => {
    setEditingUser(member);
    setForm({
      first_name: member.first_name || member.full_name?.split(' ')[0] || '',
      last_name: member.last_name || member.full_name?.split(' ').slice(1).join(' ') || '',
      date_of_birth: member.date_of_birth || '',
      official_email: member.email || '',
      personal_email: member.personal_email || '',
      official_phone: member.official_phone || member.phone || '',
      personal_phone: member.personal_phone || '',
      password: '',
      role: member.role || 'case_officer',
      designation_id: member.designation_id || '',
      compensation: member.compensation || emptyCompensation('full_time', member.role),
    });
    setShowEditModal(true);
  };

  const sanitizeCompensation = (comp) => {
    if (!comp) return comp;
    const toNum = (v) => (v === '' || v === null || v === undefined) ? null : Number(v);
    return {
      ...comp,
      monthly_salary: toNum(comp.monthly_salary),
      salary_payment_day: comp.salary_payment_day ? Number(comp.salary_payment_day) : null,
      per_close_amount: toNum(comp.per_close_amount),
      per_case_rates: (comp.per_case_rates || []).map(r => ({
        ...r, amount: toNum(r.amount) ?? 0,
      })),
      commission_tiers: (comp.commission_tiers || []).map(t => ({
        ...t,
        min_volume: t.min_volume === '' ? 0 : Number(t.min_volume),
        max_volume: toNum(t.max_volume),
        commission_amount: toNum(t.commission_amount) ?? 0,
      })),
    };
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.official_email || !form.password || !form.role) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        compensation: sanitizeCompensation(form.compensation),
      };
      await staffAPI.createStaff(payload);
      toast.success('Staff member created successfully');
      setShowAddModal(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create staff member');
    } finally { setSaving(false); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        date_of_birth: form.date_of_birth || null,
        personal_email: form.personal_email || null,
        official_phone: form.official_phone || null,
        personal_phone: form.personal_phone || null,
        role: form.role,
        designation_id: form.designation_id || null,
        compensation: sanitizeCompensation(form.compensation),
      };
      await staffAPI.updateStaff(editingUser.id, payload);
      toast.success('Staff member updated successfully');
      setShowEditModal(false);
      setEditingUser(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update staff member');
    } finally { setSaving(false); }
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Delete ${member.full_name}? This action cannot be undone.`)) return;
    try {
      await staffAPI.deleteStaff(member.id);
      toast.success('Staff member deleted');
      fetchAll();
    } catch { toast.error('Failed to delete staff member'); }
  };

  const filteredStaff = staff.filter(u => {
    const matchSearch = u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const roleStats = STAFF_ROLES.map(r => ({
    ...r,
    count: staff.filter(u => u.role === r.value).length,
  }));

  const getDesignationName = (id) => designations.find(d => d.id === id)?.name || '';

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="main-content flex items-center justify-center">
          <Loader2 className="animate-spin text-slate-400" size={40} />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content" data-testid="users-management">

        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Staff Management</h1>
            <p className="text-slate-500 text-sm">Manage team members, roles, and compensation</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowDesignationPanel(true)}
              className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-all flex items-center gap-2"
            >
              <Tag size={16} />
              Designations
            </button>
            {canManageStaff && (
              <button
                onClick={openAdd}
                className="px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-all flex items-center gap-2"
                data-testid="invite-user-button"
              >
                <UserPlus size={18} />
                Add Staff
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {roleStats.map(role => (
            <div key={role.value} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{role.label.split(' / ')[0]}s</p>
                  <p className="text-2xl font-bold text-slate-900">{role.count}</p>
                </div>
                <div className={`${role.color} p-3 rounded-lg`}>
                  <Users size={20} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 flex items-center gap-4">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none text-sm"
            />
          </div>
          <select
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg bg-white text-sm"
          >
            <option value="all">All Roles</option>
            {STAFF_ROLES.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        {/* Staff Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Staff Member</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Official Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Role & Designation</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Compensation</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredStaff.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-500">No staff members found</td>
                </tr>
              ) : filteredStaff.map(member => {
                const comp = member.compensation;
                const compSummary = !comp ? '—' :
                  comp.employment_type === 'full_time'
                    ? `₹${Number(comp.monthly_salary || 0).toLocaleString('en-IN')}/mo`
                    : comp.contractor_payment_model === 'per_case'
                    ? `Per-case (${(comp.per_case_rates || []).length} rates)`
                    : comp.contractor_payment_model === 'per_close'
                    ? `₹${Number(comp.per_close_amount || 0).toLocaleString('en-IN')}/close`
                    : `Tiered commission`;
                const compType = !comp ? null :
                  comp.employment_type === 'full_time' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700';

                return (
                  <tr key={member.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center font-semibold text-slate-700">
                          {member.full_name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{member.full_name}</p>
                          <p className="text-xs text-slate-500">{member.official_phone || member.phone || 'No phone'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700">{member.email}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[member.role] || 'bg-slate-100 text-slate-700'}`}>
                        {ROLE_LABELS[member.role] || member.role}
                      </span>
                      {(member.designation_name || getDesignationName(member.designation_id)) && (
                        <p className="text-xs text-slate-500 mt-1">{member.designation_name || getDesignationName(member.designation_id)}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {comp && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${compType}`}>
                          {compSummary}
                        </span>
                      )}
                      {!comp && <span className="text-xs text-slate-400">Not set</span>}
                    </td>
                    <td className="px-6 py-4">
                      {member.is_active !== false ? (
                        <div className="flex items-center gap-1.5 text-green-600 text-sm"><CheckCircle size={14} />Active</div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-red-500 text-sm"><XCircle size={14} />Inactive</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {(isSuperAdmin || (isAdmin && member.role !== 'super_admin')) && (
                          <button onClick={() => openEdit(member)} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all" title="Edit">
                            <Edit size={16} />
                          </button>
                        )}
                        {isSuperAdmin && member.role !== 'super_admin' && member.id !== currentUser?.id && (
                          <button onClick={() => handleDelete(member)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-all" title="Delete">
                            <Trash2 size={16} />
                          </button>
                        )}
                        {!isSuperAdmin && member.role === 'super_admin' && (
                          <span className="p-2 text-slate-300" title="Protected"><Shield size={16} /></span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Designation Panel */}
        {showDesignationPanel && (
          <DesignationPanel onClose={() => { setShowDesignationPanel(false); fetchAll(); }} />
        )}

        {/* Add Staff Modal */}
        {showAddModal && (
          <StaffModal
            title="Add Staff Member"
            form={form}
            setForm={setForm}
            onSubmit={handleCreate}
            onClose={() => setShowAddModal(false)}
            saving={saving}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            designations={designations}
            visaPrograms={visaPrograms}
            isSuperAdmin={isSuperAdmin}
            isCreate={true}
          />
        )}

        {/* Edit Staff Modal */}
        {showEditModal && editingUser && (
          <StaffModal
            title={`Edit — ${editingUser.full_name}`}
            form={form}
            setForm={setForm}
            onSubmit={handleUpdate}
            onClose={() => { setShowEditModal(false); setEditingUser(null); }}
            saving={saving}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            designations={designations}
            visaPrograms={visaPrograms}
            isSuperAdmin={isSuperAdmin}
            isCreate={false}
          />
        )}
      </div>
    </div>
  );
}

// ─── Staff Modal ──────────────────────────────────────────────────────────────

function StaffModal({ title, form, setForm, onSubmit, onClose, saving, showPassword, setShowPassword, designations, visaPrograms, isSuperAdmin, isCreate }) {
  const update = (field, val) => setForm(f => ({ ...f, [field]: val }));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
              <UserPlus size={20} className="text-slate-700" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{title}</h2>
              <p className="text-xs text-slate-500">Fill in all required fields marked with *</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} /></button>
        </div>

        {/* Body */}
        <form onSubmit={onSubmit} noValidate className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">

            {/* Personal Information */}
            <div>
              <SectionHeader icon={Users} title="Personal Information" />
              <div className="grid grid-cols-2 gap-4">
                <FieldGroup label="First Name" required>
                  <input value={form.first_name} onChange={e => update('first_name', e.target.value)} placeholder="John" className={inputCls} required />
                </FieldGroup>
                <FieldGroup label="Last Name" required>
                  <input value={form.last_name} onChange={e => update('last_name', e.target.value)} placeholder="Doe" className={inputCls} required />
                </FieldGroup>
                <FieldGroup label="Date of Birth">
                  <input type="date" value={form.date_of_birth} onChange={e => update('date_of_birth', e.target.value)} className={inputCls} />
                </FieldGroup>
                <FieldGroup label="Personal Mobile">
                  <input type="tel" value={form.personal_phone} onChange={e => update('personal_phone', e.target.value)} placeholder="+91 98765 43210" className={inputCls} />
                </FieldGroup>
              </div>
            </div>

            {/* Professional Details */}
            <div>
              <SectionHeader icon={Briefcase} title="Professional Details" />
              <div className="grid grid-cols-2 gap-4">
                <FieldGroup label="Official Email (Login)" required>
                  <input type="email" value={form.official_email} onChange={e => update('official_email', e.target.value)} placeholder="john@company.com" className={isCreate ? inputCls : disabledInputCls} required disabled={!isCreate} />
                </FieldGroup>
                <FieldGroup label="Personal Email">
                  <input type="text" value={form.personal_email} onChange={e => update('personal_email', e.target.value)} placeholder="john@gmail.com" className={inputCls} />
                </FieldGroup>
                <FieldGroup label="Official Mobile">
                  <input type="tel" value={form.official_phone} onChange={e => update('official_phone', e.target.value)} placeholder="Company-issued number" className={inputCls} />
                </FieldGroup>
                {isCreate && (
                  <FieldGroup label="Password" required>
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => update('password', e.target.value)} placeholder="••••••••" className={inputCls + " pr-10"} required />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </FieldGroup>
                )}
                <FieldGroup label="Role" required>
                  <select value={form.role} onChange={e => { update('role', e.target.value); update('compensation', emptyCompensation(form.compensation?.employment_type || 'full_time', e.target.value)); }} className={inputCls} disabled={!isSuperAdmin} required>
                    {STAFF_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </FieldGroup>
                <FieldGroup label="Designation">
                  <select value={form.designation_id} onChange={e => update('designation_id', e.target.value)} className={inputCls}>
                    <option value="">Select Designation</option>
                    {designations.map(d => <option key={d.id} value={d.id}>{d.name}{d.department ? ` (${d.department})` : ''}</option>)}
                  </select>
                </FieldGroup>
              </div>
            </div>

            {/* Compensation */}
            <div>
              <CompensationSection
                comp={form.compensation}
                onChange={comp => update('compensation', comp)}
                role={form.role}
                visaPrograms={visaPrograms}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 flex-shrink-0">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-all text-sm font-medium">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium transition-all">
              {saving ? <><Loader2 size={16} className="animate-spin" />Saving...</> : <><Save size={16} />{isCreate ? 'Create Staff Member' : 'Save Changes'}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
